import { modApi } from "../api";
import {
  MOD_ACTION_LABELS,
  type ModActionId,
  actionsFor,
  runGhostAction,
  runModAction,
} from "../moderation/actions";
import { ROLE_LABELS, escapeHtml, formatUntil } from "../moderation/format";
import { bindGhostBanButtons, ghostBanListHtml } from "../moderation/ghostBans";
import { bindReportButtons, reportHtml } from "../moderation/reports";
import type { Session } from "../session";
import type { GhostBan, OnlineGhost, OnlinePlayer, Report } from "../types";
import { confirmAction } from "./ConfirmDialog";
import { requireElement } from "./Hud";

/**
 * Painel de moderacao do jogo (moderadores e admins): lista quem esta online
 * e aplica as acoes, sempre passando pelo modal de confirmacao. Resolve
 * quando o painel fecha.
 */
export function showModerationPanel(
  session: Session,
  onReportCount: (open: number) => void = () => {},
  initialTab: "online" | "reports" | "ghostBans" = "online",
): Promise<void> {
  return new Promise((resolve) => {
    const { token, user: me } = session;
    const role = me.role;
    const root = requireElement<HTMLDivElement>("overlay-root");
    const overlay = document.createElement("div");
    overlay.className = "overlay";
    overlay.innerHTML = `
      <div class="panel mod-panel">
        <div class="profile-header">
          <div>
            <h1>Moderação</h1>
            <p class="sub mod-sub" id="mod-count">Carregando…</p>
          </div>
          <button type="button" id="mod-refresh" class="secondary small">Atualizar</button>
        </div>
        <div class="tabs" role="tablist">
          <button type="button" role="tab" data-tab="online">Online</button>
          <button type="button" role="tab" data-tab="reports">Denúncias <span id="mod-report-count"></span></button>
          <button type="button" role="tab" data-tab="ghostBans">Fantasmas banidos</button>
        </div>
        <p class="notice hidden" id="mod-notice"></p>
        <div id="mod-tab-online">
          <div class="field mod-search">
            <input id="mod-filter" type="search" placeholder="Filtrar por nome ou IP…" autocomplete="off" />
          </div>
          <ul class="mod-list" id="mod-list"></ul>
          <h2 class="mod-section">Fantasmas <span class="mod-handle" id="mod-ghost-count"></span></h2>
          <ul class="mod-list" id="mod-ghosts"></ul>
        </div>
        <div id="mod-tab-reports" class="hidden">
          <ul class="mod-list report-list" id="mod-reports"></ul>
        </div>
        <div id="mod-tab-ghostBans" class="hidden">
          <ul class="mod-list" id="mod-ghost-bans"></ul>
          <p class="sub-hint">O banimento de fantasma vale para o IP e só impede a entrada sem conta.</p>
        </div>
        ${role === "admin" ? `<a class="link-btn mod-admin-link" href="/admin" target="_blank" rel="noopener">Abrir painel administrativo ↗</a>` : ""}
        <button type="button" id="mod-close" class="secondary">Fechar</button>
      </div>
    `;
    root.replaceChildren(overlay);

    const $ = <T extends HTMLElement>(sel: string) => overlay.querySelector(sel) as T;
    const list = $<HTMLUListElement>("#mod-list");
    const count = $<HTMLParagraphElement>("#mod-count");
    const notice = $<HTMLParagraphElement>("#mod-notice");
    const filter = $<HTMLInputElement>("#mod-filter");

    let players: OnlinePlayer[] = [];
    let ghosts: OnlineGhost[] = [];
    let ghostBans: GhostBan[] = [];
    let reports: Report[] = [];
    let expanded: string | null = null;
    let dialogOpen = false;
    let tab = initialTab;

    const setNotice = (text: string | null, level: "ok" | "error" = "ok") => {
      notice.classList.toggle("hidden", !text);
      notice.className = `notice ${level}${text ? "" : " hidden"}`;
      notice.textContent = text ?? "";
    };

    const render = () => {
      const term = filter.value.trim().toLowerCase();
      const shown = players.filter(
        (p) => !term || p.name.toLowerCase().includes(term) || p.username.toLowerCase().includes(term),
      );
      count.textContent = `${players.length} ${players.length === 1 ? "jogador" : "jogadores"} na cidade`;
      list.innerHTML =
        shown.length === 0
          ? `<li class="empty">${players.length ? "Ninguém com esse nome" : "Ninguém online"}</li>`
          : shown.map((p) => rowHtml(p)).join("");
      renderGhosts(term);
    };

    // Os outros jogadores veem os fantasmas sem nome; aqui vem o apelido e o
    // IP, que e por onde o banimento pega.
    const ghostList = $<HTMLUListElement>("#mod-ghosts");
    const renderGhosts = (term: string) => {
      const shown = ghosts.filter((g) => !term || g.name.toLowerCase().includes(term) || g.ip.includes(term));
      $<HTMLSpanElement>("#mod-ghost-count").textContent = ghosts.length ? `(${ghosts.length})` : "";
      ghostList.innerHTML =
        shown
          .map(
            (g) => `
          <li class="mod-row">
            <div class="mod-row-main">
              <div class="mod-who"><strong>${escapeHtml(g.name)}</strong><span class="mod-handle">${escapeHtml(g.ip)}</span></div>
              <div class="mod-actions mod-actions-inline">
                <button type="button" class="secondary small danger" data-ghost-action="kick" data-id="${escapeHtml(g.id)}">Expulsar</button>
                <button type="button" class="secondary small danger" data-ghost-action="ban" data-id="${escapeHtml(g.id)}">Banir IP</button>
              </div>
            </div>
          </li>`,
          )
          .join("") || `<li class="empty">${ghosts.length ? "Nenhum fantasma com esse nome" : "Nenhum fantasma na cidade"}</li>`;
    };

    const ghostBanList = $<HTMLUListElement>("#mod-ghost-bans");
    const renderGhostBans = () => {
      ghostBanList.innerHTML = ghostBanListHtml(ghostBans);
      bindGhostBanButtons(ghostBanList, token, ghostBans, async (message) => {
        setNotice(message || "Feito.", "ok");
        await load();
      });
    };

    const rowHtml = (p: OnlinePlayer) => {
      const self = p.id === me.id;
      const badges = [
        p.role !== "player" ? `<span class="badge role-${p.role}">${ROLE_LABELS[p.role]}</span>` : "",
        p.mute ? `<span class="badge warn" title="silenciado">silenciado ${formatUntil(p.mute.expiresAt)}</span>` : "",
      ].join("");
      const handle = p.name === p.username ? "" : `<span class="mod-handle">@${escapeHtml(p.username)}</span>`;
      const toggle = self
        ? `<span class="mod-handle">você</span>`
        : p.canModerate
          ? `<button type="button" class="secondary small" data-toggle="${p.id}" aria-expanded="${expanded === p.id}">Ações</button>`
          : "";
      const actions =
        expanded === p.id && p.canModerate
          ? `<div class="mod-actions">${availableActions(p)
              .map(
                (a) =>
                  `<button type="button" class="secondary small${isDanger(a) ? " danger" : ""}" data-action="${a}" data-id="${p.id}">${MOD_ACTION_LABELS[a]}</button>`,
              )
              .join("")}</div>`
          : "";
      return `
        <li class="mod-row">
          <div class="mod-row-main">
            <div class="mod-who"><strong>${escapeHtml(p.name)}</strong>${handle}${badges}</div>
            ${toggle}
          </div>
          ${actions}
        </li>`;
    };

    // Na lista do jogo so entra o que faz sentido para quem esta online: quem
    // esta aqui nao esta suspenso nem banido, entao sem "remover suspensao".
    const availableActions = (p: OnlinePlayer): ModActionId[] =>
      actionsFor(role).filter((a) => {
        if (a === "unsuspend" || a === "unban") return false;
        if (a === "mute") return !p.mute;
        if (a === "unmute") return !!p.mute;
        if (a === "resetName") return p.name !== p.username; // sem apelido, nada a resetar
        return true;
      });

    const reportList = $<HTMLUListElement>("#mod-reports");
    const renderReports = () => {
      reportList.innerHTML =
        reports.map((r) => reportHtml(r, { actions: true, targetLabel: "Punir…" })).join("") ||
        `<li class="empty">Nenhuma denúncia aberta</li>`;
      bindReportButtons(reportList, token, reports, async (message) => {
        setNotice(message, "ok");
        await load();
      });
    };

    // "Punir…" numa denuncia: escolhe a acao e cai no modal dela. Serve para
    // quem ja saiu da cidade (avisar e expulsar so fazem sentido online).
    reportList.addEventListener("click", async (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-report-target]");
      const report = reports.find((r) => r.targetId === button?.dataset.reportTarget);
      if (!report?.targetId) return;
      const online = players.some((p) => p.id === report.targetId);
      const options = actionsFor(role).filter(
        (a) => !["unmute", "unsuspend", "unban"].includes(a) && (online || (a !== "warn" && a !== "kick")),
      );
      dialogOpen = true;
      let chosen: ModActionId | null = null;
      await confirmAction({
        title: `Ação contra ${report.targetName}`,
        message: online ? "A pessoa está na cidade agora." : "A pessoa não está na cidade agora.",
        confirmLabel: "Continuar",
        fields: [{ name: "action", label: "Ação", type: "select", options: options.map((a) => ({ value: a, label: MOD_ACTION_LABELS[a] })) }],
        run: async (v) => {
          chosen = v.action as ModActionId;
        },
      });
      if (chosen) {
        const target = { id: report.targetId, name: report.targetName, username: report.targetName };
        const result = await runModAction(chosen, target, token, role);
        if (result !== null) setNotice(result || "Feito.", "ok");
      }
      dialogOpen = false;
    });

    const setTab = (next: typeof tab) => {
      tab = next;
      overlay.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((b) =>
        b.setAttribute("aria-selected", String(b.dataset.tab === tab)),
      );
      $<HTMLDivElement>("#mod-tab-online").classList.toggle("hidden", tab !== "online");
      $<HTMLDivElement>("#mod-tab-reports").classList.toggle("hidden", tab !== "reports");
      $<HTMLDivElement>("#mod-tab-ghostBans").classList.toggle("hidden", tab !== "ghostBans");
    };
    overlay.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((b) =>
      b.addEventListener("click", () => {
        setNotice(null);
        setTab(b.dataset.tab as typeof tab);
      }),
    );
    setTab(tab);

    const load = async () => {
      try {
        const [online, open, banned] = await Promise.all([
          modApi.online(token),
          modApi.reports(token, "open"),
          modApi.ghostBans(token),
        ]);
        players = online.players;
        ghosts = online.ghosts ?? [];
        ghostBans = banned.bans;
        reports = open.reports;
        $<HTMLSpanElement>("#mod-report-count").textContent = open.open ? `(${open.open})` : "";
        onReportCount(open.open);
        render();
        renderReports();
        renderGhostBans();
      } catch (error) {
        setNotice(error instanceof Error ? error.message : "Não foi possível carregar a lista.", "error");
      }
    };

    list.addEventListener("click", async (event) => {
      const button = (event.target as HTMLElement).closest("button");
      if (!button) return;
      if (button.dataset.toggle) {
        expanded = expanded === button.dataset.toggle ? null : button.dataset.toggle;
        render();
        return;
      }
      const target = players.find((p) => p.id === button.dataset.id);
      const action = button.dataset.action as ModActionId | undefined;
      if (!target || !action) return;

      dialogOpen = true;
      const result = await runModAction(action, target, token, role);
      dialogOpen = false;
      if (result === null) return;
      setNotice(result || "Feito.", "ok");
      expanded = null;
      await load();
    });

    ghostList.addEventListener("click", async (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-ghost-action]");
      const ghost = ghosts.find((g) => g.id === button?.dataset.id);
      if (!button || !ghost) return;
      dialogOpen = true;
      const result = await runGhostAction(button.dataset.ghostAction as "kick" | "ban", ghost, token);
      dialogOpen = false;
      if (result === null) return;
      setNotice(result || "Feito.", "ok");
      await load();
    });

    filter.addEventListener("input", render);
    // Digitar no filtro nao pode mexer o avatar nem abrir o chat.
    overlay.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") event.stopPropagation();
    });
    $<HTMLButtonElement>("#mod-refresh").addEventListener("click", () => {
      setNotice(null);
      void load();
    });

    const close = () => {
      document.removeEventListener("keydown", onKey);
      root.replaceChildren();
      resolve();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !dialogOpen) {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener("keydown", onKey);
    $<HTMLButtonElement>("#mod-close").addEventListener("click", close);

    void load();
  });
}

function isDanger(action: ModActionId): boolean {
  return action === "kick" || action === "suspend" || action === "ban";
}
